import { useState, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { supabase } from '../lib/supabase';
import { 
  Search, 
  Mail, 
  Smartphone, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  X, 
  User, 
  Clock, 
  BookOpen, 
  Info, 
  RefreshCw, 
  Copy, 
  Check, 
  Eye, 
  BellRing,
  ShieldCheck
} from 'lucide-react';

interface AuditLog {
  id: string;
  created_at: string;
  staff_id: string | null;
  recipient_name: string | null;
  recipient_email: string | null;
  type: 'email' | 'push';
  event_type: string;
  title: string | null;
  body_preview: string | null;
  status: 'success' | 'failed' | 'dispatched';
  error_details: string | null;
  org_id: string | null;
}

export function AuditView() {
  const { team, categories, updateTeam } = useStore();
  const [subTab, setSubTab] = useState<'staff' | 'logs' | 'docs'>('staff');
  
  // Filtering & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [logTypeFilter, setLogTypeFilter] = useState<'all' | 'email' | 'push'>('all');
  const [logStatusFilter, setLogStatusFilter] = useState<'all' | 'success' | 'failed' | 'dispatched'>('all');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string | null>(null);

  // Audit Logs State
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [previewTab, setPreviewTab] = useState<'visual' | 'raw'>('visual');

  // Load audit logs from database
  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const { data, error } = await supabase
        .from('notification_audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) {
        console.error("Error fetching audit logs:", error);
      } else if (data) {
        setLogs(data as AuditLog[]);
      }
    } catch (err) {
      console.error("Exception fetching audit logs:", err);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    fetchLogs();

    // Subscribe to realtime notification_audit_logs table inserts
    const channel = supabase.channel('audit-logs-realtime')
      .on(
        'postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'notification_audit_logs' }, 
        (payload) => {
          const newLog = payload.new as AuditLog;
          setLogs(prev => [newLog, ...prev].slice(0, 300));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleCopyText = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Helper to resolve category name for a team member
  const getTeamCategoryName = (catId?: string) => {
    if (!catId) return 'Non Assigné';
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.name : 'Catégorie inconnue';
  };

  // Filtered team list
  const filteredTeam = team.filter(s => {
    const matchesSearch = 
      (s.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (s.email || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = selectedCategory === 'all' || s.categoryId === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Filtered audit logs
  const filteredLogs = logs.filter(log => {
    // 1. Search Query filter (matches recipient_name, recipient_email, title, body_preview)
    const matchesSearch = searchQuery === '' || 
      (log.recipient_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (log.recipient_email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (log.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (log.body_preview || '').toLowerCase().includes(searchQuery.toLowerCase());

    // 2. Log Type filter
    const matchesType = logTypeFilter === 'all' || log.type === logTypeFilter;

    // 3. Status filter
    const matchesStatus = logStatusFilter === 'all' || log.status === logStatusFilter;

    // 4. Team filter (from clicking 'Voir les logs' in Team Grid)
    const matchesTeam = !selectedTeamFilter || log.staff_id === selectedTeamFilter || log.recipient_email?.toLowerCase().trim() === selectedTeamFilter.toLowerCase().trim();

    return matchesSearch && matchesType && matchesStatus && matchesTeam;
  });

  const triggerDescriptions: Record<string, { title: string; trigger: string; channels: string[]; description: string; template: string }> = {
    'briefing': {
      title: 'Morning Briefing (Briefing matinal)',
      trigger: 'Tous les matins à 08:00 (Cron scheduler)',
      channels: ['Email', 'Push Notification'],
      description: 'Envoie un récapitulatif complet de tous les shifts planifiés pour le collaborateur durant la journée en cours.',
      template: 'Bonjour {Name}, voici votre programme du jour : Shift chez {Point} de {Start} à {End}... (avec lien d\'accès PWA)'
    },
    'rsvp': {
      title: 'Shift Proposal RSVP (Proposition de shift)',
      trigger: 'Lorsqu\'un manager propose ou assigne un shift à un collaborateur',
      channels: ['Email', 'Push Notification'],
      description: 'Demande au collaborateur d\'accepter ou de décliner la proposition de shift. Contient un lien d\'action rapide.',
      template: 'Une nouvelle proposition de shift est disponible pour le {Date}. Répondez dès maintenant en ouvrant l\'application.'
    },
    'countdown': {
      title: '30-Min Countdown Reminder (Rappel avant shift)',
      trigger: '30 minutes avant le début d\'un shift programmé',
      channels: ['Email', 'Push Notification'],
      description: 'Rappelle au collaborateur que son shift va bientôt démarrer et l\'invite à se préparer pour le check-in.',
      template: 'Votre shift au point d\'opération {Point} commence dans 30 minutes. Pensez à faire votre check-in à votre arrivée !'
    },
    'no-show': {
      title: '30-Min Late Alert (Alerte retard / Absence)',
      trigger: '30 minutes après le début théorique d\'un shift si aucun check-in n\'a été validé',
      channels: ['Email', 'Push Notification'],
      description: 'Alerte immédiate envoyée au collaborateur pour lui signaler son retard, et notifie également le manager de la catégorie.',
      template: 'Alerte Check-in manquant : Le shift de {Name} à {Point} a commencé depuis 30 minutes sans check-in enregistré.'
    },
    'message': {
      title: 'Direct Message Alert (Nouveau message instantané)',
      trigger: 'Envoi d\'un message direct si le destinataire n\'est pas actif sur la plateforme',
      channels: ['Push Notification', 'Email (si non distribué en Push)'],
      description: 'Notification en temps réel informant de la réception d\'un message sur la messagerie de l\'application.',
      template: '{Sender} vous a envoyé un message : "{MessageContent}"'
    },
    'otp': {
      title: 'One-Time Password (OTP de connexion)',
      trigger: 'Lors d\'une tentative de connexion par email par un administrateur, manager ou collaborateur',
      channels: ['Email uniquement'],
      description: 'Envoie le code d\'accès temporaire sécurisé à 6 chiffres pour valider l\'identité de l\'utilisateur.',
      template: 'Votre code de vérification à usage unique est : {Code}. Ce code expire dans 10 minutes.'
    },
    'manager_verification': {
      title: 'Manager Verification (Validation d\'accès manager)',
      trigger: 'Lorsqu\'un nouveau manager est enregistré sur une catégorie',
      channels: ['Email uniquement'],
      description: 'Envoie un email contenant le lien d\'accès unique et exclusif pour revendiquer le rôle de manager.',
      template: 'Vous avez été invité à gérer la catégorie {Category}. Cliquez sur ce lien pour configurer votre accès...'
    },
    'swap': {
      title: 'Shift Swap Request (Demande d\'échange de shift)',
      trigger: 'Lorsqu\'un collaborateur soumet une demande d\'échange de shift avec un collègue',
      channels: ['Email', 'Push Notification'],
      description: 'Demande à l\'autre collaborateur s\'il souhaite reprendre le shift proposé dans le cadre d\'un échange.',
      template: '{Sender} vous propose d\'échanger son shift du {Date} ({Point}). Répondez depuis votre espace collaborateur.'
    },
    'marketplace': {
      title: 'Marketplace Broadcast (Bourse aux shifts)',
      trigger: 'Lorsqu\'un shift vacant est publié sur le marché libre',
      channels: ['Email', 'Push Notification'],
      description: 'Diffuse une alerte à l\'ensemble des collaborateurs qualifiés et disponibles pour cette catégorie.',
      template: 'Un shift est disponible sur la bourse aux shifts chez {Point} le {Date}. Ouvrez l\'application pour vous positionner.'
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 h-full flex flex-col overflow-hidden relative bg-[#0A0B0E] text-slate-300">
      <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-indigo-600/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent flex items-center gap-2">
            <BellRing className="text-indigo-400 w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0 animate-pulse" />
            <span>Audits & Notifications</span>
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm mt-1">
            Supervisez les délivrances de notifications, l'état d'enregistrement des appareils (PWA/Push) et la documentation des flux.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex bg-[#14161B] p-1 rounded-xl border border-[#2D3139] self-start md:self-auto">
          <button
            onClick={() => { setSubTab('staff'); setSelectedTeamFilter(null); }}
            className={`px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${subTab === 'staff' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/10' : 'text-slate-400 hover:text-white'}`}
          >
            Statut Appareils Team
          </button>
          <button
            onClick={() => setSubTab('logs')}
            className={`px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${subTab === 'logs' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/10' : 'text-slate-400 hover:text-white'}`}
          >
            Historique d'Audit {logs.length > 0 && <span className="ml-1.5 bg-white/20 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{logs.length}</span>}
          </button>
          <button
            onClick={() => { setSubTab('docs'); setSelectedTeamFilter(null); }}
            className={`px-4 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-all ${subTab === 'docs' ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/10' : 'text-slate-400 hover:text-white'}`}
          >
            Schéma & Modèles
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 bg-[#14161B]/50 backdrop-blur-sm border border-[#2D3139] rounded-2xl overflow-hidden flex flex-col relative z-10 shadow-xl">
        
        {/* SUBTAB 1: TEAM DEVICE STATUS GRID */}
        {subTab === 'staff' && (
          <div className="flex-1 flex flex-col overflow-hidden p-6">
            {/* Filter Bar */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 flex-none">
              <div className="relative col-span-2">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <input
                  type="text"
                  placeholder="Rechercher par nom ou email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#0A0B0E]/80 border border-[#2D3139] hover:border-[#2D3139]/80 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-300 placeholder-slate-500 outline-none transition-all"
                />
              </div>

              <div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full bg-[#0A0B0E]/80 border border-[#2D3139] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl px-4 py-2.5 text-sm text-slate-300 outline-none transition-all"
                >
                  <option value="all">Toutes les catégories</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Team list table */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-[#2D3139] text-slate-500 text-xs font-bold uppercase tracking-wider sticky top-0 bg-[#14161B] z-10">
                    <th className="py-3 px-4">Membre du Team</th>
                    <th className="py-3 px-4">Catégorie</th>
                    <th className="py-3 px-4 text-center">Vérification Email</th>
                    <th className="py-3 px-4 text-center">PWA Installée</th>
                    <th className="py-3 px-4 text-center">Push FCM Actif</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2D3139]/40">
                  {filteredTeam.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-500">
                        Aucun collaborateur ne correspond aux filtres.
                      </td>
                    </tr>
                  ) : (
                    filteredTeam.map((s) => (
                      <tr key={s.id} className="hover:bg-[#1A1D23]/30 transition-colors group">
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center font-bold text-indigo-400">
                              {s.photo ? (
                                <img src={s.photo} alt={s.name} className="w-full h-full object-cover rounded-xl" />
                              ) : (
                                s.name.substring(0, 2).toUpperCase()
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-semibold text-white truncate text-sm">{s.name}</p>
                              <p className="text-slate-500 text-xs truncate">{s.email || 'Aucun email'}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4 text-sm font-medium text-slate-400">
                          {getTeamCategoryName(s.categoryId)}
                        </td>
                        <td className="py-4 px-4 text-center">
                          {s.emailConfirmed ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                              <ShieldCheck className="w-3.5 h-3.5" /> Vérifié
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-500/10 text-slate-400 border border-[#2D3139]">
                              Non vérifié
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-center">
                          {s.pwaInstalled ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                              <CheckCircle className="w-3.5 h-3.5" /> Oui
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-500/10 text-slate-400 border border-[#2D3139]">
                              Non
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-center">
                          {s.fcmToken ? (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" title={s.fcmToken}>
                              <Smartphone className="w-3.5 h-3.5" /> Actif
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-500/10 text-slate-400 border border-[#2D3139]">
                              Inactif
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div className="flex justify-end gap-2">
                            {!s.emailConfirmed && (
                              <button
                                onClick={() => {
                                  if (window.confirm(`Confirmer manuellement l'email de ${s.name} (${s.email || 'sans email'}) ?`)) {
                                    updateTeam(s.id, {
                                      emailConfirmed: true,
                                      verificationCode: null,
                                      verificationCodeSentAt: null
                                    });
                                  }
                                }}
                                className="px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg border border-emerald-500/30 text-xs font-semibold flex items-center gap-1 inline-flex"
                              >
                                <ShieldCheck className="w-3.5 h-3.5" /> Valider l'email
                              </button>
                            )}
                            <button
                              onClick={() => {
                                  setSelectedTeamFilter(s.email || s.id);
                                  setSubTab('logs');
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity px-3 py-1.5 bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 rounded-lg border border-indigo-500/30 text-xs font-semibold"
                            >
                              Voir les logs
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* SUBTAB 2: AUDIT LOGS HISTORY */}
        {subTab === 'logs' && (
          <div className="flex-1 flex flex-col overflow-hidden p-6">
            {/* Filter Bar */}
            <div className="flex flex-col gap-4 mb-6 flex-none">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="relative col-span-1 sm:col-span-2">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    placeholder="Filtrer par destinataire, email ou mot clé..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-[#0A0B0E]/80 border border-[#2D3139] hover:border-[#2D3139]/80 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl pl-10 pr-4 py-2 text-sm text-slate-300 placeholder-slate-500 outline-none transition-all"
                  />
                </div>

                <div>
                  <select
                    value={logTypeFilter}
                    onChange={(e) => setLogTypeFilter(e.target.value as any)}
                    className="w-full bg-[#0A0B0E]/80 border border-[#2D3139] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl px-4 py-2 text-sm text-slate-300 outline-none transition-all"
                  >
                    <option value="all">Tous les canaux (Email & Push)</option>
                    <option value="email">Email uniquement</option>
                    <option value="push">Push Notification uniquement</option>
                  </select>
                </div>

                <div>
                  <select
                    value={logStatusFilter}
                    onChange={(e) => setLogStatusFilter(e.target.value as any)}
                    className="w-full bg-[#0A0B0E]/80 border border-[#2D3139] focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl px-4 py-2 text-sm text-slate-300 outline-none transition-all"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="success">Succès</option>
                    <option value="failed">Échec</option>
                    <option value="dispatched">Distribué (Dispatched)</option>
                  </select>
                </div>
              </div>

              {/* Active filters / breadcrumb status */}
              {(selectedTeamFilter || searchQuery || logTypeFilter !== 'all' || logStatusFilter !== 'all') && (
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#2D3139]/40 text-xs">
                  <span className="text-slate-500">Filtres actifs :</span>
                  
                  {selectedTeamFilter && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-indigo-500/10 text-indigo-400 border border-indigo-500/20">
                      Collaborateur: {selectedTeamFilter}
                      <button onClick={() => setSelectedTeamFilter(null)} className="hover:text-white ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  
                  {logTypeFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-slate-500/10 text-slate-300 border border-[#2D3139]">
                      Type: {logTypeFilter}
                      <button onClick={() => setLogTypeFilter('all')} className="hover:text-white ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}

                  {logStatusFilter !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md bg-slate-500/10 text-slate-300 border border-[#2D3139]">
                      Statut: {logStatusFilter}
                      <button onClick={() => setLogStatusFilter('all')} className="hover:text-white ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}

                  <button 
                    onClick={() => {
                      setSearchQuery('');
                      setLogTypeFilter('all');
                      setLogStatusFilter('all');
                      setSelectedTeamFilter(null);
                    }}
                    className="text-indigo-400 hover:text-indigo-300 ml-auto font-semibold"
                  >
                    Réinitialiser tout
                  </button>
                </div>
              )}
            </div>

            {/* Table or Loading */}
            {loadingLogs ? (
              <div className="flex-1 flex flex-col items-center justify-center text-slate-500">
                <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin mb-4" />
                <p>Chargement des logs d'audit...</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-[#2D3139] text-slate-500 text-xs font-bold uppercase tracking-wider sticky top-0 bg-[#14161B] z-10">
                      <th className="py-3 px-4">Date & Heure</th>
                      <th className="py-3 px-4 text-center">Canal</th>
                      <th className="py-3 px-4">Destinataire</th>
                      <th className="py-3 px-4">Déclencheur / Contenu</th>
                      <th className="py-3 px-4">Statut</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2D3139]/40">
                    {filteredLogs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-slate-500">
                          Aucun log de notification enregistré.
                        </td>
                      </tr>
                    ) : (
                      filteredLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-[#1A1D23]/30 transition-colors group">
                          <td className="py-4 px-4 text-xs font-medium text-slate-400">
                            {new Date(log.created_at).toLocaleString('fr-FR')}
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className={`inline-flex p-1.5 rounded-lg ${log.type === 'email' ? 'bg-cyan-500/10 text-cyan-400' : 'bg-indigo-500/10 text-indigo-400'}`} title={log.type}>
                              {log.type === 'email' ? <Mail className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-sm">
                            <div className="font-semibold text-white">{log.recipient_name || 'Inconnu'}</div>
                            <div className="text-slate-500 text-xs">{log.recipient_email}</div>
                          </td>
                          <td className="py-4 px-4 text-sm max-w-xs md:max-w-md">
                            <div className="flex items-center gap-1.5">
                              <span className="px-1.5 py-0.5 rounded bg-slate-800 border border-[#2D3139] text-[10px] font-mono text-slate-400">
                                {log.event_type}
                              </span>
                              <span className="font-medium text-white truncate max-w-[180px] sm:max-w-none">
                                {log.title || 'Sans titre'}
                              </span>
                            </div>
                            <p className="text-slate-500 text-xs truncate mt-0.5">{log.body_preview || 'Aucun aperçu'}</p>
                          </td>
                          <td className="py-4 px-4">
                            {log.status === 'success' && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                <CheckCircle className="w-3.5 h-3.5" /> Succès
                              </span>
                            )}
                            {log.status === 'dispatched' && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                <Info className="w-3.5 h-3.5 animate-pulse" /> Distribué
                              </span>
                            )}
                            {log.status === 'failed' && (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/20">
                                <XCircle className="w-3.5 h-3.5" /> Échec
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <button
                              onClick={() => {
                                setSelectedLog(log);
                                setPreviewTab('visual');
                              }}
                              className="px-3 py-1.5 bg-[#1C1F26] border border-[#2D3139] hover:bg-[#2D3139] text-slate-300 rounded-lg text-xs font-semibold flex items-center gap-1 ml-auto"
                            >
                              <Eye className="w-3.5 h-3.5" /> Inspecter
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* SUBTAB 3: DOCUMENTATION & SCHEMAS */}
        {subTab === 'docs' && (
          <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
            <div className="max-w-4xl mx-auto space-y-6">
              
              <div className="bg-[#1C1F26] border border-[#2D3139] rounded-2xl p-6">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                  <BookOpen className="text-indigo-400 w-5 h-5" />
                  Guide d'Architecture des Notifications
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  L'application utilise un système hybride combinant des <strong>Triggers PostgreSQL directs</strong> (envois instantanés) et un <strong>Cron Scheduler</strong> hébergé sous forme d'Edge Function Supabase (envois asynchrones réguliers). Toutes les communications de type email transitent par <strong>Resend</strong>, tandis que les notifications push transitent par <strong>Firebase Cloud Messaging (FCM)</strong>.
                </p>
              </div>

              {/* Documentation grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(triggerDescriptions).map(([key, item]) => (
                  <div key={key} className="bg-[#0A0B0E]/80 border border-[#2D3139] rounded-xl p-5 hover:border-indigo-500/30 transition-all flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start gap-2 mb-3">
                        <h4 className="font-bold text-white text-sm tracking-wide">{item.title}</h4>
                        <div className="flex gap-1 flex-shrink-0">
                          {item.channels.map((chan, idx) => (
                            <span key={idx} className={`text-[8px] font-bold px-1.5 py-0.5 rounded uppercase border ${
                              chan.includes('Email') 
                                ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' 
                                : 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                            }`}>
                              {chan.split(' ')[0]}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2 text-xs">
                        <div className="flex items-center gap-1.5 text-slate-400">
                          <Clock className="w-3.5 h-3.5 text-indigo-400 flex-shrink-0" />
                          <span><strong>Déclenchement :</strong> {item.trigger}</span>
                        </div>
                        <p className="text-slate-400 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-[#2D3139]/40 bg-[#050505]/40 p-2.5 rounded-lg border border-[#2D3139]/30">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Structure Type</span>
                      <code className="text-[11px] font-mono text-indigo-300 block select-all break-words leading-tight">{item.template}</code>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        )}

      </div>

      {/* DETAILED LOG INSPECTOR DRAWER / MODAL */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="w-full max-w-2xl bg-[#14161B] border border-[#2D3139] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-5 border-b border-[#2D3139] flex items-center justify-between bg-[#1C1F26]">
              <div className="flex items-center gap-2">
                <span className={`inline-flex p-1.5 rounded-lg ${selectedLog.type === 'email' ? 'bg-cyan-500/10 text-cyan-400' : 'bg-indigo-500/10 text-indigo-400'}`}>
                  {selectedLog.type === 'email' ? <Mail className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
                </span>
                <div>
                  <h3 className="font-bold text-white text-base">Inspection du log</h3>
                  <p className="text-[10px] font-mono text-slate-500">{selectedLog.id}</p>
                </div>
              </div>
              
              <button 
                onClick={() => setSelectedLog(null)}
                className="p-1.5 hover:bg-[#2D3139] rounded-lg text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
              
              {/* Grid 1: Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-[#0A0B0E] p-4 rounded-xl border border-[#2D3139]">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Destinataire</span>
                  <div className="flex items-center gap-2 text-sm text-white">
                    <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="font-bold">{selectedLog.recipient_name || 'Non spécifié'}</span>
                  </div>
                  <div className="text-slate-400 text-xs mt-1 truncate pl-6">{selectedLog.recipient_email}</div>
                </div>

                <div className="bg-[#0A0B0E] p-4 rounded-xl border border-[#2D3139]">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest block mb-1">Statut d'envoi</span>
                  <div className="flex items-center gap-2">
                    {selectedLog.status === 'success' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        <CheckCircle className="w-4 h-4" /> Succès
                      </span>
                    )}
                    {selectedLog.status === 'dispatched' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                        <Info className="w-4 h-4" /> Dispatched / En attente
                      </span>
                    )}
                    {selectedLog.status === 'failed' && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-500/10 text-red-400 border border-red-500/20">
                        <XCircle className="w-4 h-4" /> Échec
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 pl-1">
                    Enregistré le {new Date(selectedLog.created_at).toLocaleString('fr-FR')}
                  </div>
                </div>
              </div>

              {/* Technical properties */}
              <div className="bg-[#0A0B0E] p-4 rounded-xl border border-[#2D3139] space-y-2.5 text-xs">
                <div className="flex justify-between border-b border-[#2D3139]/40 pb-2">
                  <span className="text-slate-500">Événement déclencheur (event_type) :</span>
                  <span className="font-semibold text-white font-mono">{selectedLog.event_type}</span>
                </div>
                <div className="flex justify-between border-b border-[#2D3139]/40 pb-2">
                  <span className="text-slate-500">Canal de notification :</span>
                  <span className="font-semibold text-white capitalize">{selectedLog.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Lien avec table Team (team_id) :</span>
                  <span className="font-semibold text-slate-400 font-mono select-all">{selectedLog.staff_id || 'Aucun'}</span>
                </div>
              </div>

              {/* Error details if failed */}
              {selectedLog.status === 'failed' && selectedLog.error_details && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-red-400 text-sm font-semibold">
                    <AlertCircle className="w-4 h-4" />
                    <span>Détails techniques de l'erreur</span>
                  </div>
                  <pre className="text-xs font-mono text-red-300 bg-[#050505] p-3 rounded-lg border border-red-950/40 overflow-x-auto whitespace-pre-wrap select-all max-h-40 custom-scrollbar">
                    {selectedLog.error_details}
                  </pre>
                </div>
              )}

              {/* Message Content Preview */}
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-[#2D3139]/50 pb-2">
                  <span className="text-sm font-bold text-white">Aperçu du contenu</span>
                  <div className="flex bg-[#0A0B0E] p-1 rounded-lg border border-[#2D3139] text-xs">
                    <button
                      onClick={() => setPreviewTab('visual')}
                      className={`px-3 py-1 rounded-md transition-all ${previewTab === 'visual' ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                      Aperçu Visuel
                    </button>
                    <button
                      onClick={() => setPreviewTab('raw')}
                      className={`px-3 py-1 rounded-md transition-all ${previewTab === 'raw' ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:text-slate-300'}`}
                    >
                      Code Source
                    </button>
                  </div>
                </div>

                <div className="bg-[#0A0B0E] rounded-xl border border-[#2D3139] overflow-hidden p-4 min-h-[160px] flex flex-col justify-center">
                  
                  {previewTab === 'visual' ? (
                    selectedLog.type === 'push' ? (
                      /* Mock mobile push message view */
                      <div className="max-w-sm mx-auto w-full bg-[#1C1F26] border border-[#2D3139] rounded-2xl p-4 shadow-xl select-none">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 rounded-md bg-indigo-600 flex items-center justify-center font-black text-white text-[10px]">
                              WS
                            </div>
                            <span className="text-[11px] font-bold text-slate-300">WE-SHIFT</span>
                          </div>
                          <span className="text-[10px] text-slate-500">À l'instant</span>
                        </div>
                        <div className="font-semibold text-xs text-white">
                          {selectedLog.title || 'Notification'}
                        </div>
                        <div className="text-slate-400 text-xs mt-0.5 line-clamp-3">
                          {selectedLog.body_preview || 'Aucun message.'}
                        </div>
                      </div>
                    ) : (
                      /* Safe sandbox rendering for HTML emails or fallback to text */
                      selectedLog.body_preview && (selectedLog.body_preview.includes('<!DOCTYPE') || selectedLog.body_preview.includes('<html') || selectedLog.body_preview.includes('<div')) ? (
                        <iframe
                          title="Preview Email"
                          srcDoc={`
                            <!DOCTYPE html>
                            <html>
                              <head>
                                <style>
                                  body {
                                    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                                    color: #2D3748;
                                    background: #F7FAFC;
                                    padding: 20px;
                                    margin: 0;
                                    font-size: 14px;
                                    line-height: 1.5;
                                  }
                                  a { color: #4F46E5; text-decoration: none; font-weight: 500; }
                                </style>
                              </head>
                              <body>
                                ${selectedLog.body_preview}
                              </body>
                            </html>
                          `}
                          className="w-full h-64 border-0 rounded-lg bg-[#F7FAFC]"
                        />
                      ) : (
                        <div className="text-xs text-slate-300 font-mono whitespace-pre-wrap leading-relaxed select-all">
                          <strong>Sujet : {selectedLog.title}</strong>
                          <br /><br />
                          {selectedLog.body_preview || '(Pas de contenu)'}
                        </div>
                      )
                    )
                  ) : (
                    /* Plaintext or code representation */
                    <div className="relative">
                      <button
                        onClick={() => handleCopyText(selectedLog.body_preview || '', 'body_copy')}
                        className="absolute top-2 right-2 p-1.5 bg-[#1C1F26] border border-[#2D3139] hover:bg-[#2D3139] rounded-lg text-slate-400 hover:text-white"
                        title="Copier le code"
                      >
                        {copiedId === 'body_copy' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                      <pre className="text-[11px] font-mono text-indigo-300 bg-[#050505] p-4 rounded-lg overflow-x-auto whitespace-pre-wrap select-all max-h-60 custom-scrollbar">
                        {selectedLog.body_preview || '(Pas de contenu)'}
                      </pre>
                    </div>
                  )}

                </div>
              </div>

            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-[#2D3139] bg-[#1C1F26]/60 flex justify-end">
              <button
                onClick={() => setSelectedLog(null)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold rounded-xl text-sm transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
